import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import {
  FaBell,
  FaTimes,
  FaCalendarAlt,
  FaClock,
  FaTicketAlt,
  FaHeart,
  FaCheck,
  FaSearch,
} from "react-icons/fa";
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../redux/actions/notifications";

const getTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
  if (diffInHours < 1) {
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    return `${diffInMinutes} min ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
  } else {
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
  }
};

const getIcon = (type) => {
  switch (type) {
    case "BOOKING":     return <FaCalendarAlt />;
    case "RESCHEDULE":  return <FaClock />;
    case "CANCELLATION": return <FaTimes />;
    case "PAYMENT":     return <FaTicketAlt />;
    case "REVIEW":      return <FaHeart />;
    default:            return <FaBell />;
  }
};

const getCategory = (type) => {
  switch (type) {
    case "BOOKING":
    case "RESCHEDULE":
    case "CANCELLATION":
    case "PAYMENT":
      return "academics";
    case "REVIEW":
      return "community";
    default:
      return "academics";
  }
};

// ─── Styled components ──────────────────────────────────────────────────────

const Wrapper = styled.div`
  padding: 16px 0;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #2d3436;
  margin: 0 0 16px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #ed1e24;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UnreadBadge = styled.span`
  background: #ed1e24;
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  border-left: 4px solid
    ${(props) => (props.unread ? (props.category === "community" ? "#a29bfe" : "#74b9ff") : "transparent")};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s;
  cursor: pointer;
  display: flex;
  gap: 12px;
  align-items: flex-start;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
`;

const IconBox = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    props.category === "community" ? "#f3e5f5" : "#e3f2fd"};
  color: ${(props) =>
    props.category === "community" ? "#7b1fa2" : "#1976d2"};
  font-size: 16px;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.h4`
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 600;
  color: #2d3436;
`;

const CardMessage = styled.p`
  margin: 0 0 6px 0;
  font-size: 13px;
  color: #636e72;
  line-height: 1.5;
`;

const CardTime = styled.span`
  font-size: 12px;
  color: #b2bec3;
`;

const UnreadDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) =>
    props.category === "community" ? "#a29bfe" : "#74b9ff"};
  flex-shrink: 0;
  margin-top: 6px;
`;

const MarkAllButton = styled.button`
  background: transparent;
  border: 1px solid #ed1e24;
  color: #ed1e24;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;

  &:hover {
    background: rgba(237, 30, 36, 0.06);
  }
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SearchWrapper = styled.div`
  position: relative;
  margin-bottom: 12px;
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #b2bec3;
  font-size: 14px;
  pointer-events: none;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 14px 10px 36px;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  font-size: 14px;
  color: #2d3436;
  background: white;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &::placeholder {
    color: #b2bec3;
  }

  &:focus {
    border-color: #ed1e24;
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const Tab = styled.button`
  padding: 7px 20px;
  border-radius: 20px;
  border: 1.5px solid ${(props) => (props.active ? "#ed1e24" : "#e0e0e0")};
  background: ${(props) => (props.active ? "#ed1e24" : "white")};
  color: ${(props) => (props.active ? "white" : "#636e72")};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #ed1e24;
    color: ${(props) => (props.active ? "white" : "#ed1e24")};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 16px;
  color: #b2bec3;
  font-size: 14px;
`;

// ─── Component ──────────────────────────────────────────────────────────────

const NotificationsFeed = () => {
  const dispatch = useDispatch();
  const { notifications: reduxNotifications, loading } = useSelector(
    (state) => state.notifications
  );
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    dispatch(getNotifications());
  }, [dispatch]);

  const notifications = reduxNotifications;
  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const handleMarkAllRead = () => {
    dispatch(markAllNotificationsRead());
  };

  const handleMarkRead = (id) => {
    dispatch(markNotificationRead(id));
  };

  const visibleNotifications = notifications.filter((n) => {
    const matchesTab = activeTab === "all" || !n.is_read;
    const query = search.toLowerCase();
    const matchesSearch =
      !query ||
      n.title.toLowerCase().includes(query) ||
      n.message.toLowerCase().includes(query);
    return matchesTab && matchesSearch;
  });

  return (
    <Wrapper>
      <TitleRow>
        <SectionTitle>
          <FaBell style={{ color: "#ed1e24" }} />
          Notifications
          {unreadCount > 0 && <UnreadBadge>{unreadCount} new</UnreadBadge>}
        </SectionTitle>
        {unreadCount > 0 && (
          <MarkAllButton onClick={handleMarkAllRead}>
            <FaCheck size={11} /> Mark all as read
          </MarkAllButton>
        )}
      </TitleRow>

      <SearchWrapper>
        <SearchIcon><FaSearch /></SearchIcon>
        <SearchInput
          type="text"
          placeholder="Search notifications..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </SearchWrapper>

      <Tabs>
        <Tab active={activeTab === "all"} onClick={() => setActiveTab("all")}>
          All
        </Tab>
        <Tab active={activeTab === "unread"} onClick={() => setActiveTab("unread")}>
          Unread {unreadCount > 0 && `(${unreadCount})`}
        </Tab>
      </Tabs>

      {visibleNotifications.length === 0 ? (
        <EmptyState>
          {search ? `No notifications match "${search}"` : "You're all caught up!"}
        </EmptyState>
      ) : (
        visibleNotifications.map((n) => {
          const category = getCategory(n.notification_type);
          return (
            <Card
              key={n.id}
              unread={!n.is_read}
              category={category}
              onClick={() => handleMarkRead(n.id)}
            >
              <IconBox category={category}>{getIcon(n.notification_type)}</IconBox>
              <Body>
                <CardTitle>{n.title}</CardTitle>
                <CardMessage>{n.message}</CardMessage>
                <CardTime>{getTimeAgo(n.created_at)}</CardTime>
              </Body>
              {!n.is_read && <UnreadDot category={category} />}
            </Card>
          );
        })
      )}
    </Wrapper>
  );
};

export default NotificationsFeed;
